import React from 'react'
import { Box, Flex } from 'components/ds'
import styles from './index.module.css'

type Props = {
  size?: 'small' | 'medium' | 'large'
  color?: string
}

/*
 * DISCO-9709: the shape morph runs as SMIL <animate> rather than CSS @keyframes
 * because WebKit does not support animating the SVG `d` property from CSS, which
 * left the logo malformed in Safari. The translate animations stay in CSS —
 * those are well supported everywhere. Do not move `d` back into the stylesheet.
 */
const MORPH_DURATION = '1.68s'

/* 0%, 6.93%, 18.81%, 78.22%, 91.09%, 100% of the CSS keyframes as fractions. */
const KEY_TIMES = '0; 0.0693; 0.1881; 0.7822; 0.9109; 1'

/*
 * SMIL takes one easing curve per interval (5), not per keyframe (6). These are
 * the two cubic-bezier curves from index.module.css; the third interval is the
 * long hold between morphs, so it stays linear.
 */
const KEY_SPLINES_PRIMARY =
  '0.36 0 0.198 1; 0.36 0 0.198 1; 0 0 1 1; 0.36 0 0.198 1; 0.36 0 0.198 1'
const KEY_SPLINES_SECONDARY =
  '0.333 0 0.667 1; 0.333 0 0.667 1; 0 0 1 1; 0.333 0 0.667 1; 0.333 0 0.667 1'

const BOTTOM_SQUARE =
  'M 50.618 -70.534 L 50.75 70.678 L 191.054 70.428 L 190.884 -70.534 Z'
const BOTTOM_PARALLELOGRAM =
  'M 105.618 -70.428 L 0.25 70.428 L 191.054 70.428 L 296.384 -70.428 Z'

const MIDDLE_SQUARE =
  'M 51.118 -70.497 L 50.75 70.678 L 191.054 70.428 L 190.884 -70.747 Z'
const MIDDLE_PARALLELOGRAM =
  'M 105.618 -70.428 L 0.25 70.428 L 191.054 70.428 L 296.384 -70.428 Z'

const TOP_SQUARE =
  'M 191.054 70.428 L 190.883 -70.534 L 50.618 -70.534 L 50.251 70.678 Z'
const TOP_PARALLELOGRAM =
  'M 191.054 70.428 L 296.383 -70.428 L 105.618 -70.428 L 0.251 70.428 Z'

/** Each shape holds, morphs out, holds, then morphs back. */
const morphValues = (square: string, parallelogram: string): string =>
  [square, square, parallelogram, parallelogram, square, square].join(';')

/**
 * Animated loading component.
 *
 * Displays three animated cube shapes that morph and stack, creating a loading effect.
 *
 * @param size - Size variant of the loader (default: 'medium')
 * @param color - Fill color for the shapes (default: 'white')
 */
const DiscoStackLoader = ({
  size = 'medium',
  color = 'black',
}: Props): React.ReactNode => {
  // Calculate dimensions based on size - maintaining aspect ratio
  const dimensions = {
    small: { width: 200, height: 100 },
    medium: { width: 300, height: 150 },
    large: { width: 640, height: 520 },
  }[size]

  return (
    <Flex
      direction="column"
      align="center"
      gap="4"
      data-testid="disco-stack-loader"
    >
      <Box
        position="relative"
        width={`${dimensions.width}px`}
        height={`${dimensions.height}px`}
      >
        <svg
          viewBox="0 0 1200 600"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
          }}
        >
          {/* Bottom Shape */}
          <g className={styles.bottomShape}>
            <path className={styles.bottomPath} fill={color} d={BOTTOM_SQUARE}>
              <animate
                attributeName="d"
                dur={MORPH_DURATION}
                repeatCount="indefinite"
                calcMode="spline"
                keyTimes={KEY_TIMES}
                keySplines={KEY_SPLINES_PRIMARY}
                values={morphValues(BOTTOM_SQUARE, BOTTOM_PARALLELOGRAM)}
              />
            </path>
          </g>

          {/* Middle Shape */}
          <g className={styles.middleShape}>
            <path className={styles.middlePath} fill={color} d={MIDDLE_SQUARE}>
              <animate
                attributeName="d"
                dur={MORPH_DURATION}
                repeatCount="indefinite"
                calcMode="spline"
                keyTimes={KEY_TIMES}
                keySplines={KEY_SPLINES_SECONDARY}
                values={morphValues(MIDDLE_SQUARE, MIDDLE_PARALLELOGRAM)}
              />
            </path>
          </g>

          {/* Top Shape */}
          <g className={styles.topShape}>
            <path className={styles.topPath} fill={color} d={TOP_SQUARE}>
              <animate
                attributeName="d"
                dur={MORPH_DURATION}
                repeatCount="indefinite"
                calcMode="spline"
                keyTimes={KEY_TIMES}
                keySplines={KEY_SPLINES_PRIMARY}
                values={morphValues(TOP_SQUARE, TOP_PARALLELOGRAM)}
              />
            </path>
          </g>
        </svg>
      </Box>
    </Flex>
  )
}

export default DiscoStackLoader
